import { apiUrl } from './config';

export interface PlanLimits {
  id: string;
  name: string;
  drawers: number;
  pads: number;
  spectators: number;
  padPasswords: boolean;
  images: boolean;
  replay: boolean;
  copy: boolean;
}

export interface User {
  id: string;
  email: string;
  username: string;
  avatarColor: string;
  emailVerified: boolean;
  settings: Record<string, unknown>;
  createdAt: number;
  plan: 'free' | 'pro';
  planUntil: number | null;
  billingSource: string | null;
  /** What this account may do. Mirrors the server; the server is what enforces. */
  limits: PlanLimits;
}

export interface PlanPrice {
  /** Minor units, as Stripe quotes them: 1999 means £19.99. */
  amount: number;
  currency: string;
  interval: string;
  intervalCount: number;
}

export interface BillingStatus {
  plan: 'free' | 'pro';
  /** Read live from Stripe, so it follows whatever the dashboard says. */
  price: PlanPrice | null;
  planUntil: number | null;
  source: string | null;
  renewing: boolean;
  limits: PlanLimits;
  plans: Record<string, PlanLimits>;
  /** False when the server has no payment keys, so no buy button should show. */
  available: boolean;
}

export interface Pad {
  id: string;
  title: string;
  ownerId: string;
  ownerName: string;
  ownerColor: string;
  isOwner: boolean;
  joinCode?: string;
  hasPassword?: boolean;
  settings: PadSettings;
  thumbnail: string | null;
  originPadId: string | null;
  createdAt: number;
  updatedAt: number;
  lastOpSeq: number;
  role?: string;
  lastOpenedAt?: number;
  opCount?: number;
}

export interface PadSettings {
  background: 'plain' | 'dots' | 'grid' | 'lines';
  allowSpectators: boolean;
  locked: boolean;
  allowGuestClearAll: boolean;
  showPeerCursors: boolean;
  showPeerViewports: boolean;
  chatEnabled: boolean;
  laserEnabled: boolean;
}

export interface Session {
  user: User;
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
}

export class ApiError extends Error {
  code: string;

  status: number;

  constructor(message: string, code: string, status: number) {
    super(message);
    this.code = code;
    this.status = status;
  }
}

const STORAGE_KEY = 'inkshare.session';
/** The app was called Inkwell first; carry those sessions over silently. */
const LEGACY_STORAGE_KEY = 'inkwell.session';

interface StoredTokens {
  accessToken: string;
  refreshToken: string;
  expiresAt: number;
}

let tokens: StoredTokens | null = loadTokens();
let refreshInFlight: Promise<StoredTokens | null> | null = null;
const listeners = new Set<(signedIn: boolean) => void>();

function loadTokens(): StoredTokens | null {
  try {
    let raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      const legacy = localStorage.getItem(LEGACY_STORAGE_KEY);
      if (legacy) {
        localStorage.setItem(STORAGE_KEY, legacy);
        localStorage.removeItem(LEGACY_STORAGE_KEY);
        raw = legacy;
      }
    }
    return raw ? (JSON.parse(raw) as StoredTokens) : null;
  } catch {
    return null;
  }
}

function saveTokens(next: StoredTokens | null) {
  tokens = next;
  try {
    if (next) localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    else localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* private browsing — stay signed in for this tab only */
  }
  for (const fn of listeners) fn(!!next);
}

export function onAuthChange(fn: (signedIn: boolean) => void) {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
}

export const hasSession = () => !!tokens;

function storeSession(session: Session) {
  saveTokens({
    accessToken: session.accessToken,
    refreshToken: session.refreshToken,
    // Refresh a minute early so a request never races the expiry.
    expiresAt: Date.now() + session.expiresIn - 60_000,
  });
  return session.user;
}

async function rawRequest(path: string, init: RequestInit, token?: string | null) {
  const headers = new Headers(init.headers);
  if (init.body !== undefined) headers.set('content-type', 'application/json');
  if (token) headers.set('authorization', `Bearer ${token}`);

  const url = apiUrl(path);

  let res: Response;
  try {
    res = await fetch(url, { ...init, headers });
  } catch {
    // fetch only rejects when the request never completed: no network, DNS
    // failure, TLS refused, or the response was blocked by CORS. The browser
    // deliberately hides which, so name the address instead of saying
    // "something went wrong" — that at least points at the right machine.
    const target = url.startsWith('http') ? new URL(url).origin : window.location.origin;
    throw new ApiError(
      `Could not reach ${target}. Check your connection — if this is the app and you are online, `
      + 'the server is not accepting requests from it.',
      'network',
      0,
    );
  }

  const text = await res.text();
  let body: any = {};
  if (text) {
    try {
      body = JSON.parse(text);
    } catch {
      // Not JSON. Almost always a proxy, a login page or an error page
      // answering instead of the API — which used to be swallowed into an
      // empty object and surface as a meaningless failure three steps later.
      throw new ApiError(
        `${new URL(url, window.location.origin).origin} answered with a web page instead of data `
        + `(HTTP ${res.status}). The API is not where the app is looking.`,
        'not_api',
        res.status,
      );
    }
  }

  if (!res.ok) throw new ApiError(body.error || 'Something went wrong.', body.code || 'error', res.status);
  return body;
}

/** Swap the refresh token for a new pair. Coalesced across callers. */
async function refresh(): Promise<StoredTokens | null> {
  if (!tokens) return null;
  if (!refreshInFlight) {
    const current = tokens.refreshToken;
    refreshInFlight = rawRequest('/auth/refresh', {
      method: 'POST',
      body: JSON.stringify({ refreshToken: current }),
    })
      .then((session: Session) => {
        storeSession(session);
        return tokens;
      })
      .catch(() => {
        saveTokens(null);
        return null;
      })
      .finally(() => { refreshInFlight = null; });
  }
  return refreshInFlight;
}

/** Authenticated request; transparently refreshes an expired access token. */
async function request(path: string, init: RequestInit = {}) {
  if (tokens && tokens.expiresAt <= Date.now()) await refresh();
  if (!tokens) throw new ApiError('Please sign in.', 'unauthorized', 401);

  try {
    return await rawRequest(path, init, tokens.accessToken);
  } catch (err) {
    if (err instanceof ApiError && err.status === 401) {
      const next = await refresh();
      if (next) return rawRequest(path, init, next.accessToken);
      saveTokens(null);
    }
    throw err;
  }
}

const post = (path: string, body?: unknown) => request(path, { method: 'POST', body: JSON.stringify(body ?? {}) });
const patch = (path: string, body: unknown) => request(path, { method: 'PATCH', body: JSON.stringify(body) });
const put = (path: string, body: unknown) => request(path, { method: 'PUT', body: JSON.stringify(body) });
const del = (path: string, body?: unknown) => request(path, { method: 'DELETE', body: JSON.stringify(body ?? {}) });

export const api = {
  // --- auth ---------------------------------------------------------------
  async register(input: { email: string; username: string; password: string }) {
    const session: Session = await rawRequest('/auth/register', { method: 'POST', body: JSON.stringify(input) });
    return storeSession(session);
  },

  async login(input: { login: string; password: string }) {
    const session: Session = await rawRequest('/auth/login', { method: 'POST', body: JSON.stringify(input) });
    return storeSession(session);
  },

  async logout() {
    const refreshToken = tokens?.refreshToken;
    saveTokens(null);
    if (refreshToken) {
      await rawRequest('/auth/logout', { method: 'POST', body: JSON.stringify({ refreshToken }) }).catch(() => {});
    }
  },

  forgotPassword: (email: string): Promise<{ ok: true; devResetLink?: string }> => rawRequest('/auth/forgot', { method: 'POST', body: JSON.stringify({ email }) }),

  async resetPassword(token: string, password: string) {
    const session: Session = await rawRequest('/auth/reset', { method: 'POST', body: JSON.stringify({ token, password }) });
    return storeSession(session);
  },

  verifyEmail: (token: string) => rawRequest('/auth/verify-email', { method: 'POST', body: JSON.stringify({ token }) }),
  resendVerification: () => post('/auth/resend-verification'),

  me: (): Promise<{ user: User }> => request('/auth/me'),
  updateMe: (input: { username?: string; avatarColor?: string; settings?: Record<string, unknown> }): Promise<{ user: User }> => patch('/auth/me', input),

  async changePassword(currentPassword: string, newPassword: string) {
    const session: Session = await post('/auth/change-password', { currentPassword, newPassword });
    return storeSession(session);
  },

  signOutEverywhere: () => post('/auth/logout-all'),

  async deleteAccount(password: string) {
    await del('/auth/me', { password });
    saveTokens(null);
  },

  // --- pads ---------------------------------------------------------------
  library: (): Promise<{ pads: Pad[] }> => request('/pads'),

  createPad: (input: { title?: string; password?: string | null }): Promise<{ pad: Pad; ticket: string }> => post('/pads', input),

  joinPad: (input: { code: string; password?: string }): Promise<{ pad: Pad; role: string; ticket: string }> => post('/pads/join', input),

  openPad: (padId: string): Promise<{ pad: Pad; role: string; ticket: string }> => post(`/pads/${padId}/open`),

  getPad: (padId: string): Promise<{ pad: Pad; bounds: unknown; occupancy: unknown }> => request(`/pads/${padId}`),

  updatePad: (padId: string, input: {
    title?: string; settings?: Partial<PadSettings>; password?: string | null; thumbnail?: string | null;
  }): Promise<{ pad: Pad }> => patch(`/pads/${padId}`, input),

  saveThumbnail: (padId: string, thumbnail: string) => put(`/pads/${padId}/thumbnail`, { thumbnail }),

  rotateCode: (padId: string): Promise<{ pad: Pad }> => post(`/pads/${padId}/rotate-code`),

  copyPad: (padId: string, title?: string): Promise<{ pad: Pad; ticket: string }> => post(`/pads/${padId}/copy`, { title }),

  billingStatus: (): Promise<BillingStatus> => request('/billing/status'),
  /** Returns the URL of the hosted payment page to send the browser to. */
  startCheckout: (): Promise<{ url: string }> => post('/billing/checkout'),
  /** Stripe's own page for changing a card or cancelling. */
  billingPortal: (): Promise<{ url: string }> => post('/billing/portal'),

  deletePad: (padId: string): Promise<{ ok: true; deleted: boolean }> => del(`/pads/${padId}`),

  chatHistory: (padId: string) => request(`/pads/${padId}/chat`),
};
